import { memo } from 'react';
import { matchPath, useLocation } from 'react-router';

import { visitPaths } from '~libs.desktop.shared.paths';

import { HealthcareFacilityDropdown } from './HealthcareFacilityDropdown';
import { HealthcareFacilityDropdownLoader } from './HealthcareFacilityDropdown.loader';
import { ServiceProvidingLocationDropdown } from './ServiceProvidingLocationDropdown';
import { ServiceProvidingLocationDropdownLoader } from './ServiceProvidingLocationDropdown.loader';
import { VisitLocationSelector } from './VisitLocation.types';

type VisitLocationProps = VisitLocationSelector & {
   readonly visitId: number;
   readonly disabled: boolean;
};

export const VisitLocation = memo(
   (props: VisitLocationProps): JSX.Element => {
      const location = useLocation();
      const isCurrentPathVisitInterview = matchPath(location.pathname, {
         path: visitPaths.getVisitInterviewPath({
            params: {
               visitId: props.visitId,
            },
         }),
      });
      const isCurrentPathPrescriptionVisit = matchPath(location.pathname, {
         path: visitPaths.getPrescriptionVisitPath({
            params: {
               visitId: props.visitId,
            },
         }),
         exact: true,
      });
      const disabled = props.disabled || !(isCurrentPathVisitInterview || isCurrentPathPrescriptionVisit);

      if (props.healthcareFacilityLocationId || props.healthcareFacilityUnitId) {
         return (
            <HealthcareFacilityDropdownLoader>
               {({ healthcareFacilityParts }) => (
                  <HealthcareFacilityDropdown
                     visitId={props.visitId}
                     disabled={disabled}
                     healthcareFacilityLocationId={props.healthcareFacilityLocationId}
                     healthcareFacilityUnitId={props.healthcareFacilityUnitId}
                     healthcareFacilityParts={healthcareFacilityParts}
                  />
               )}
            </HealthcareFacilityDropdownLoader>
         );
      }

      return (
         <ServiceProvidingLocationDropdownLoader withRemovedLocations={disabled}>
            {({ serviceProvidingLocation }) => (
               <ServiceProvidingLocationDropdown
                  visitId={props.visitId}
                  serviceProvidingLocationId={props.serviceProvidingLocationId}
                  disabled={disabled}
                  serviceProvidingLocations={serviceProvidingLocation}
               />
            )}
         </ServiceProvidingLocationDropdownLoader>
      );
   },
   (prevProps, nextProps) => prevProps.visitId !== nextProps.visitId
);

VisitLocation.displayName = 'VisitLocation';
